// src/components/CompareSelector.js
import React, { useState } from 'react';
import { buildBatteryOptions, groupBatteryOptionsByDevice, selectionKey } from '../utils/compareSelection';
import './CompareSelector.css';

function CompareSelector({ devices, onCompareSelected, onChangeGroup }) {
  const batteryOptions = buildBatteryOptions(devices);
  const groupedOptions = groupBatteryOptionsByDevice(batteryOptions);
  const deviceIds = Object.keys(groupedOptions);

  const [selectedKeys, setSelectedKeys] = useState(new Set());

  const toggleBattery = (option) => {
    const key = selectionKey(option);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleDevice = (deviceId) => {
    const deviceOptions = groupedOptions[deviceId];
    const allSelected = deviceOptions.every(o => selectedKeys.has(selectionKey(o)));
    setSelectedKeys(prev => {
      const next = new Set(prev);
      deviceOptions.forEach(o => {
        const key = selectionKey(o);
        if (allSelected) {
          next.delete(key);
        } else {
          next.add(key);
        }
      });
      return next;
    });
  };

  const handleCompare = () => {
    const selections = batteryOptions.filter(o => selectedKeys.has(selectionKey(o)));
    onCompareSelected(selections);
  };

  return (
    <div className="historic-data-view">
      <h2>Compare Batteries</h2>
      <p className="subtitle">Select any combination of batteries to view side-by-side</p>

      {deviceIds.length === 0 && (
        <div className="no-data">
          <p>No batteries available to compare.</p>
        </div>
      )}

      {deviceIds.length > 0 && (
        <div className="results">
          <div className="results-header">
            <h3>Available Batteries ({batteryOptions.length})</h3>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <span className="summary-item">Selected: {selectedKeys.size}</span>
              {onChangeGroup && (
                <button type="button" className="device-action-button" onClick={onChangeGroup}>
                  Change Group
                </button>
              )}
            </div>
          </div>

          <div className="device-groups">
            {deviceIds.map(deviceId => {
              const deviceOptions = groupedOptions[deviceId];
              const selectedCount = deviceOptions.filter(o => selectedKeys.has(selectionKey(o))).length;
              const allSelected = selectedCount === deviceOptions.length;
              return (
                <div key={deviceId} className="device-group">
                  <div className="device-header">
                    <h4>{deviceOptions[0].deviceLabel} — {deviceOptions[0].model}</h4>
                    <button type="button" className="device-action-button" onClick={() => toggleDevice(deviceId)}>
                      {allSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="battery-grid">
                    {deviceOptions.map(option => {
                      const key = selectionKey(option);
                      const checked = selectedKeys.has(key);
                      return (
                        <label key={key} className={checked ? 'battery-item selected' : 'battery-item'}>
                          <input
                             type="checkbox"
                             checked={checked}
                             onChange={() => toggleBattery(option)}
                          />
                          {option.batteryLabel}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className="select-button"
            onClick={handleCompare}
            disabled={selectedKeys.size === 0}
          >
            Compare Selected ({selectedKeys.size})
          </button>
        </div>
      )}
    </div>
  );
}

export default CompareSelector;
